/**
 * User-defined calculated rows on the KPI grid (`/api/kpi/custom-metrics`).
 *
 * Separate from `kpiService` because it addresses a different resource with a
 * different lifetime: the table is read over and over, these are a small
 * DEFINITION set that changes only when a user adds or removes a row. The rows
 * themselves come back inside the ordinary table payload, so nothing here is
 * on the read path.
 *
 * All access goes through `apiFetch` and every response is parsed at the
 * boundary; a shape that does not match the contract is a failure, not
 * something to render against.
 */

import {
	type KpiCustomMetric,
	type KpiUnit,
	parseKpiCustomMetric,
	parseKpiCustomMetricList,
} from "../types/kpi";
import { apiFetch } from "../utils/apiClient";

/** The server rejected the expression (or the label/unit) it was given. */
const HTTP_UNPROCESSABLE = 422;

export interface KpiCustomMetricInput {
	label: string;
	expr: string;
	unit: KpiUnit;
}

/**
 * What happened to a save — a create or an edit; the outcomes are identical.
 *
 * A rejected EXPRESSION is not a failed request: the server has read the
 * formula, decided it cannot evaluate it, and said why. That message is the
 * only thing that can get the user to a working expression, so it is carried
 * out of the service rather than flattened into "could not save" — and the
 * dialog stays open around it, because everything the user typed is still the
 * best starting point for the next attempt.
 *
 * One union for both verbs so the dialog that serves both modes has one
 * contract to branch on rather than two that differ only in a word.
 */
export type KpiCustomMetricSaveOutcome =
	| { kind: "saved"; metric: KpiCustomMetric }
	| { kind: "invalid"; message: string }
	| { kind: "failed" };

export type KpiCustomMetricDeleteOutcome = { kind: "deleted" } | { kind: "failed" };

const GENERIC_INVALID = "That expression could not be used.";

/**
 * Pull the server's explanation out of a 422 body.
 *
 * FastAPI sends `{detail: "<message>"}` for the hand-raised rejection this
 * contract pins, but its own validation errors put a LIST of objects in the
 * same field. Rendering `[object Object]` at the user would be worse than
 * saying nothing specific, so anything that is not a plain string falls back to
 * a generic line.
 */
async function invalidMessage(resp: Response): Promise<string> {
	try {
		const body: unknown = await resp.json();
		const detail = (body as { detail?: unknown } | null)?.detail;
		if (typeof detail === "string" && detail.trim().length > 0) return detail;
		return GENERIC_INVALID;
	} catch {
		return GENERIC_INVALID;
	}
}

class KpiCustomMetricsService {
	/** Every custom row this client has defined. Null on failure. */
	async list(): Promise<KpiCustomMetric[] | null> {
		try {
			const resp = await apiFetch("/api/kpi/custom-metrics");
			if (!resp.ok) return null;
			return parseKpiCustomMetricList(await resp.json());
		} catch {
			return null;
		}
	}

	/** Define a new calculated row. */
	async create(
		input: KpiCustomMetricInput,
	): Promise<KpiCustomMetricSaveOutcome> {
		return this.save("/api/kpi/custom-metrics", "POST", input);
	}

	/**
	 * Change an existing row's label, expression or unit.
	 *
	 * The KEY does not move — the server keeps it stable — which is what makes
	 * this an edit rather than a delete and a re-create: expressions in other
	 * custom rows that name this one keep naming it.
	 */
	async update(
		id: string,
		input: KpiCustomMetricInput,
	): Promise<KpiCustomMetricSaveOutcome> {
		return this.save(
			`/api/kpi/custom-metrics/${encodeURIComponent(id)}`,
			"PUT",
			input,
		);
	}

	/** Shared body of create/update: the two differ only in verb and URL. */
	private async save(
		url: string,
		method: "POST" | "PUT",
		input: KpiCustomMetricInput,
	): Promise<KpiCustomMetricSaveOutcome> {
		try {
			const resp = await apiFetch(url, {
				method,
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(input),
			});
			if (resp.status === HTTP_UNPROCESSABLE) {
				return { kind: "invalid", message: await invalidMessage(resp) };
			}
			if (!resp.ok) return { kind: "failed" };
			const parsed = parseKpiCustomMetric(await resp.json());
			// A 2xx whose body does not match the contract is still a failure: the
			// row may exist, but nothing here can say what was saved.
			return parsed === null
				? { kind: "failed" }
				: { kind: "saved", metric: parsed };
		} catch {
			return { kind: "failed" };
		}
	}

	/** Remove a calculated row. `id` is the public UUID from `list`. */
	async remove(id: string): Promise<KpiCustomMetricDeleteOutcome> {
		try {
			const resp = await apiFetch(
				`/api/kpi/custom-metrics/${encodeURIComponent(id)}`,
				{ method: "DELETE" },
			);
			return resp.ok ? { kind: "deleted" } : { kind: "failed" };
		} catch {
			return { kind: "failed" };
		}
	}
}

export const kpiCustomMetricsService = new KpiCustomMetricsService();
export default kpiCustomMetricsService;
